import React, { useState } from "react";
import { ScreenHead, DqMarker } from "./psPrimitives";

// Full-only step spec — ported verbatim from the revQ Practice Setup v11 mock (FULL{}).
// Nothing here saves; these are design specs for endpoints that don't exist yet.
const YN = ["Yes", "No"];

export const FULL_SPEC = {
  acctype: {
    lede: "How the practice is structured, and who works in it.",
    secs: [
      { t: "How is the practice structured?", radio: [
        ["One practice, one patient panel", "All providers share one patient population and bill under one entity."],
        ["Multiple separate practices", "Billing company, MSO or management group — each client keeps its own patients, payers and billing.", true],
        ["One practice today — may add groups later", "Acquisitions or new divisions whose patients must stay separate."],
      ], note: "Multiple separate practices shows Clients and Link providers to clients. This account is an organization with 115 clients." },
      { t: "Team", team: true },
      { t: "Team security", sw: ["Require two-factor authentication for all sub-logins", "Restrict billers to their assigned providers", "Auto-expire pending invitations after 7 days", "Notify the account owner when a sub-login signs in from a new device"] },
    ],
  },
  practice: {
    lede: "Your legal entity details — these flow onto every claim.",
    lens: "a W-9, 837 or CMS-1500 (Box 25, 33)",
    secs: [
      { t: "Legal entity", f: [["Legal business name"], ["DBA / practice name"], ["Tax ID (EIN)"], ["Entity type", ["PLLC", "PC", "LLC", "Corporation", "Sole proprietor"]], ["State of registration"]] },
      { t: "Government identifiers", f: [["Group NPI (Type 2)"], ["Taxonomy code"], ["Medicare PTAN (group)"], ["Medicaid provider #"], ["CLIA number (if lab services)"]] },
      { t: "Contact", f: [["Main phone"], ["Fax"], ["Practice email"], ["Website"], ["Business address"]] },
    ],
  },
  locations: {
    lede: "Where care is delivered. Each location maps to a place-of-service code on claims.",
    lens: "a locations list (CSV)",
    migr: ["63 locations found in your migrated claims", "Review them and map each to a place-of-service code."],
    secs: [
      { t: "Location", f: [["Location name"], ["POS code", ["11 — Office", "02 — Telehealth, not in home", "10 — Telehealth, in home", "22 — Outpatient hospital", "31 — Skilled nursing facility"]], ["Address"], ["Phone"], ["Location NPI (if distinct)"]], add: "+ Add another location" },
      { t: "Telehealth", f: [["Platform"], ["Modifier default", ["95 — Synchronous telemedicine", "GT", "None"]]] },
    ],
  },
  clearing: {
    lede: "Connect the clearinghouse that sends your claims.",
    secs: [
      { t: "Clearinghouse account", f: [["Clearinghouse", ["Availity", "Change Healthcare", "Waystar", "Office Ally", "TriZetto"]], ["Submitter ID"], ["Receiver ID (if assigned)"], ["Portal username"], ["SFTP host (if file-based)"], ["SFTP username"]] },
      { t: "Enrollment", f: [["Existing EDI agreements?", ["Yes — transfer to revQ", "No"]], ["Submit a test claim first?", ["Yes — test batch before go-live", "No"]]] },
    ],
  },
  eraeft: {
    lede: "Enroll with payers to receive remittances and payments.",
    secs: [
      { t: "Deposit account", f: [["Bank name"], ["Account type", ["Business checking", "Business savings"]], ["Routing number"], ["Account number"], ["Account holder name"], ["Voided check / bank letter", "file"]] },
      { t: "Remit delivery", f: [["ERA delivery", ["Through the clearinghouse", "Direct from each payer"]], ["Auto-post ERAs in revQ?", ["Yes — flag exceptions", "No — I post manually"]]] },
    ],
  },
  fees: {
    lede: "What you charge for each code.",
    lens: "a fee schedule (CSV)",
    migr: ["174 CPT codes billed in your migrated claims", "revQ can rebuild your charges from them — confirm the amounts instead of typing each one."],
    secs: [
      { t: "Fee strategy", f: [["Fee basis", ["Custom fee schedule (uploaded)", "% of Medicare"]], ["Medicare multiplier (if % based)"], ["Effective date", "date"], ["Annual review reminder", ["Yes — remind me each January", "No"]]] },
    ],
  },
  history: {
    lede: "Bring in claims you've already submitted.",
    secs: [{ t: "Source system", f: [["Previous billing system"], ["Export format available", ["837 / 835 files", "CSV export", "PDF reports"]], ["How far back to import?", ["30 days", "90 days", "180 days", "1 year"]], ["Transfer open claims / active A·R?", YN]] }],
  },
  balances: {
    lede: "Open patient balances and their aging.",
    secs: [{ t: "Outstanding balances", f: [["Hold statements during transition?", ["Yes — hold 30 days", "No"]], ["Existing payment plans to honor?", ["Yes — import terms", "No"]]] }],
  },
  records: {
    lede: "Who can see medical records.",
    lens: "835 remittances or denial EOBs",
    secs: [
      { t: "Records responsibility", f: [["Who provides records on denial?", ["Practice staff handles it", "revQ pulls from the EHR", "Hospital / HIE portal"]], ["Portal username"], ["Portal password"]] },
      { t: "Records denial escalation", f: [["If a claim is denied for medical records, revQ should", ["Escalate once; close temporarily if no response in 10 days", "Route to me"]]] },
    ],
  },
  prefs: {
    lede: "Defaults for how claims are built.",
    lens: "a practice-rules PDF, or an 837 / 835 to infer your preferences",
    secs: [
      { t: "Out-of-network", f: [["When a provider is out-of-network, revQ should", ["Bill the patient at full charge", "Hold for my review", "Bill under a participating provider"]], ["Participating provider to bill under"]] },
      { t: "Claim routing defaults", f: [["Commercial claims", ["Electronic via clearinghouse", "Paper CMS-1500"]], ["Medicare claims", ["Electronic via clearinghouse", "Paper CMS-1500"]], ["Medicaid claims", ["Electronic via clearinghouse", "Paper CMS-1500"]]] },
      { t: "Holds & approvals", sw: ["Hold Medicare claims until secondary coverage is verified", "Hold claims above a dollar threshold for my approval"] },
    ],
  },
  auths: {
    lede: "How prior authorizations and adjustments are handled.",
    lens: "an 837 or CMS-1500 (Box 23)",
    secs: [
      { t: "Prior authorization", sw: ["Attempt retro-authorization before writing off"], f: [["If a claim is denied for PCP authorization, revQ should", ["Obtain the referral / PCP auth and resubmit", "Route to me"]], ["For hospital claims denied for pre-auth, revQ should", ["Request retro-auth from the facility / payer", "Route to me"]]] },
      { t: "Auto-adjustment permissions", sw: ["Adjust non-payable CPT codes per the payer contract", "Adjust CPT codes denied as non-covered under the provider specialty contract", "Adjust CPT codes denied as inclusive", "Adjust CPT codes denied as mutually exclusive procedures", "Allow the practice 15 days to respond on ICD-not-covered denials, then adjust"] },
    ],
  },
  mods: {
    lede: "Which modifiers revQ may add.",
    lens: "an 837 or CMS-1500 (Box 24D)",
    mods: true,
    secs: [
      { t: "Administrative modifiers", sw: ["25 — significant, separately identifiable E/M", "59 — distinct procedural service", "XE — separate encounter", "GP — physical therapy plan of care"] },
      { t: "Anatomical modifiers", sw: ["LT — left side", "RT — right side", "50 — bilateral procedure"] },
      { t: "Other", f: [["Additional permitted modifiers"]] },
    ],
  },
  stmts: {
    lede: "Statement cycle and self-pay terms.",
    lens: "835 remittances",
    secs: [
      { t: "Patient responsibility billing", sw: ["Bill deductible for Medicare patients", "Bill coinsurance for Medicare patients", "Bill deductible / coinsurance / copay for commercial patients", "Bill deductible / coinsurance / copay for Medicaid patients", "Send statements to telemedicine patients", "Alert the practice when a patient carries a balance"] },
      { t: "Statement cadence", f: [["Statement frequency", ["Every 30 days", "Every 15 days", "Every 45 days"]], ["Statements before collections", ["2", "3", "4"]]] },
      { t: "Self-pay", f: [["Collect payment upfront?", ["Yes — at check-in", "No"]], ["Upfront threshold amount"], ["If self-pay goes unanswered", ["Escalate once; close the claim after 10 days", "Route to me"]]] },
    ],
  },
  review: {
    lede: "Review everything and authorize revQ to submit.",
    secs: [
      { t: "Agreements", sw: ["I consent to revQ storing this information to configure and operate billing on my behalf", "I declare that, to the best of my knowledge, the information provided is true and accurate"] },
      { t: "Authorized signature", f: [["Full name"], ["Title"], ["Date", "date"], ["Signature (type your full name)"]], note: "Suggested home for exam-default attestations: a named signer with title and date, instead of the logged-in analyst.", noteDq: 20 },
    ],
  },
};

const INPUT = "w-full rounded-[10px] border border-[#E9E9EC] bg-white px-3 py-2 text-[13px] text-[var(--ink)] outline-none focus:border-[#5856D6] transition";

function FField({ f }) {
  const [label, opt] = f;
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[12px] text-[var(--gray2)]">{label}</label>
      {Array.isArray(opt) ? (
        <select className={INPUT} defaultValue="">{opt.map((o) => <option key={o}>{o}</option>)}</select>
      ) : opt === "date" ? (
        <input type="date" className={INPUT} />
      ) : opt === "file" ? (
        <button type="button" className="rounded-[10px] border border-[#E9E9EC] bg-white px-3 py-2 text-[13px] text-[var(--ink2)] text-left hover:border-[var(--gray3)]">Upload…</button>
      ) : (
        <input className={INPUT} />
      )}
    </div>
  );
}

function SwitchRow({ label }) {
  const [on, setOn] = useState(false);
  return (
    <div className="flex items-center justify-between gap-3 py-2.5 border-t border-[#E9E9EC] first:border-t-0">
      <span className="text-[13px] text-[var(--ink2)]">{label}</span>
      <button type="button" role="switch" aria-checked={on} onClick={() => setOn((v) => !v)}
        className={`w-9 h-5 rounded-full flex-none transition relative ${on ? "bg-[var(--ink)]" : "bg-[#D2D2D7]"}`}>
        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${on ? "left-[18px]" : "left-0.5"}`} />
      </button>
    </div>
  );
}

function RadioGroup({ items }) {
  const [sel, setSel] = useState(items.findIndex((r) => r[2]) >= 0 ? items.findIndex((r) => r[2]) : 0);
  return (
    <div className="flex flex-col gap-2">
      {items.map((r, i) => {
        const on = sel === i;
        return (
          <button key={i} type="button" onClick={() => setSel(i)}
            className={`flex items-start gap-3 text-left rounded-[12px] border px-3.5 py-3 transition ${on ? "border-[#5856D6] bg-[rgba(88,86,214,.04)]" : "border-[#E9E9EC] bg-white hover:border-[var(--gray3)]"}`}>
            <span className={`w-[18px] h-[18px] rounded-full flex-none mt-0.5 border-[1.5px] flex items-center justify-center ${on ? "border-[#5856D6]" : "border-[var(--gray3)]"}`}>
              {on ? <span className="w-2 h-2 rounded-full bg-[#5856D6]" /> : null}
            </span>
            <span>
              <span className="block text-[13.5px] font-semibold text-[var(--ink)]">{r[0]}</span>
              <span className="block text-[12.5px] text-[var(--gray2)] mt-0.5">{r[1]}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

function MigrCard({ title, sub, onOpenDq }) {
  return (
    <div className="rounded-[12px] border border-[#E9E9EC] bg-white px-4 py-3 flex items-center gap-4 flex-wrap mb-4">
      <div className="flex-1 min-w-[220px]">
        <div className="text-[13.5px] font-semibold text-[var(--ink)]">{title}</div>
        <div className="text-[12.5px] text-[var(--gray2)] mt-0.5">{sub}</div>
      </div>
      <div className="flex items-center gap-3">
        <button type="button" className="rounded-[10px] border border-[#E9E9EC] bg-white px-3 py-1.5 text-[12.5px] font-semibold text-[var(--ink2)] hover:border-[var(--gray3)]">Review</button>
        <DqMarker n={43} onOpen={onOpenDq} />
      </div>
    </div>
  );
}

function TeamSection({ profile }) {
  const owner = [profile?.first_name, profile?.last_name].filter(Boolean).join(" ") || profile?.email || "You";
  const initials = owner.split(" ").map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
  return (
    <div className="rounded-[14px] overflow-hidden border border-[#E9E9EC] bg-white">
      <div className="flex items-center gap-3 px-4 py-3.5">
        <span className="w-[38px] h-[38px] rounded-full bg-[#F1F1F4] text-[var(--gray2)] font-bold text-[12.5px] flex items-center justify-center flex-none">{initials}</span>
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-semibold text-[var(--ink)]">{profile?.email} <span className="text-[var(--gray2)] font-normal">(you)</span></div>
          <div className="text-[12.5px] text-[var(--gray2)] mt-0.5">Account owner · full access</div>
        </div>
      </div>
      <div className="flex items-end gap-3 px-4 py-3.5 border-t border-[#E9E9EC]">
        <div className="flex-1"><FField f={["Invite by email"]} /></div>
        <div className="w-[160px]"><FField f={["Role", ["Provider", "Biller", "Front desk", "Account owner"]]} /></div>
        <button type="button" className="rounded-[10px] border border-[#E9E9EC] bg-white px-3 py-2 text-[12.5px] font-semibold text-[var(--ink2)] hover:border-[var(--gray3)]">Invite</button>
      </div>
    </div>
  );
}

export function FullPane({ step, profile, onOpenDq }) {
  const spec = FULL_SPEC[step.id];
  const [fm, setFm] = useState("manual"); // lens | manual

  if (!spec) {
    return (
      <div className="fade-up">
        <ScreenHead eyebrow={`${step.group} · Full state`} title={step.name} lede={step.dq ? undefined : ""} />
        <div className="rounded-[16px] border border-dashed border-[#E9E9EC] bg-white py-10 px-6 text-center text-[13px] text-[var(--gray2)]">
          Nothing in the API backs this step yet.
          <div className="mt-3 flex justify-center"><DqMarker n={step.dq} onOpen={onOpenDq} /></div>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-up">
      <ScreenHead eyebrow={`${step.group} · Full state`} title={step.name} lede={spec.lede} />

      {/* Needs API banner */}
      <div className="rounded-[12px] border px-4 py-3 flex items-center justify-between gap-3 mb-4" style={{ borderColor: "rgba(88,86,214,.28)", background: "rgba(88,86,214,.04)" }}>
        <span className="text-[12.5px] text-[var(--ink2)]">Needs API — this step is a spec from the original revQ mock; nothing here saves.</span>
        <DqMarker n={step.dq} onOpen={onOpenDq} />
      </div>

      {step.optional ? (
        <div className="rounded-[12px] border border-[#E9E9EC] bg-[#FBFBFD] px-4 py-3 flex items-center justify-between gap-3 mb-4 text-[12.5px] text-[var(--gray2)]">
          <span><b className="text-[var(--ink2)]">Optional</b> — not required to go live. Finish it anytime.</span>
          <button type="button" className="text-[12.5px] font-semibold text-[#4646b8] hover:underline">Remind me later</button>
        </div>
      ) : null}

      {spec.migr ? <MigrCard title={spec.migr[0]} sub={spec.migr[1]} onOpenDq={onOpenDq} /> : null}

      {spec.lens ? (
        <div className="flex items-center gap-3 mb-4">
          <div className="inline-flex rounded-[10px] border border-[#E9E9EC] overflow-hidden">
            {[["lens", "Auto-fill with Lens"], ["manual", "Enter manually"]].map(([k, label]) => (
              <button key={k} type="button" onClick={() => setFm(k)}
                className={`text-[12.5px] font-semibold px-3 py-1.5 transition ${fm === k ? "bg-[var(--ink)] text-white" : "text-[var(--ink2)] hover:bg-black/[.04]"}`}>{label}</button>
            ))}
          </div>
          <DqMarker n={49} onOpen={onOpenDq} />
        </div>
      ) : null}

      {spec.lens && fm === "lens" ? (
        <div className="rounded-[16px] border border-dashed border-[#E9E9EC] bg-white py-10 px-6 text-center">
          <div className="text-[15px] font-bold text-[var(--ink)]">Drop {spec.lens}</div>
          <p className="text-[13px] text-[var(--gray2)] mt-1.5 max-w-[52ch] mx-auto">Lens reads it and fills this step; you review every field before it's used.</p>
          <button type="button" className="mt-4 btn-ink">Choose file…</button>
        </div>
      ) : (
        <div className="flex flex-col gap-7">
          {spec.secs.map((sec, si) => (
            <section key={si}>
              <div className="text-[13px] font-semibold text-[var(--gray2)] mb-2.5">{sec.t}</div>
              {sec.radio ? <RadioGroup items={sec.radio} /> : null}
              {sec.team ? <TeamSection profile={profile} /> : null}
              {sec.sw ? (
                <div className="rounded-[14px] border border-[#E9E9EC] bg-white px-4">
                  {sec.sw.map((t) => <SwitchRow key={t} label={t} />)}
                </div>
              ) : null}
              {sec.f ? (
                <div className={`grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3.5 ${sec.sw ? "mt-3.5" : ""}`}>
                  {sec.f.map((f) => <FField key={f[0]} f={f} />)}
                </div>
              ) : null}
              {sec.add ? (
                <div className="mt-3"><button type="button" className="text-[12.5px] font-semibold text-[#4646b8] hover:underline" onClick={() => onOpenDq?.(step.dq)}>{sec.add}</button></div>
              ) : null}
              {sec.note ? (
                <p className="text-[12.5px] text-[var(--gray2)] mt-3">{sec.note}{sec.noteDq ? <> <button onClick={() => onOpenDq?.(sec.noteDq)} className="text-[#4646b8] font-semibold">DQ-{sec.noteDq}</button></> : null}</p>
              ) : null}
            </section>
          ))}
          {spec.mods ? (
            <section>
              <div className="text-[13px] font-semibold text-[var(--gray2)] mb-2">Written rules that mention a modifier</div>
              <p className="text-[12.5px] text-[var(--gray2)] mb-2">These switches govern modifiers. Written pre-claim rules that mention one are listed here to reconcile. <button onClick={() => onOpenDq?.(50)} className="text-[#4646b8] font-semibold">DQ-50</button></p>
              <p className="text-[12.5px] text-[var(--gray2)]">Reconciliation appears once written rules are loaded.</p>
            </section>
          ) : null}
        </div>
      )}
    </div>
  );
}
